import { TokenService } from "./token.service";
import { getLogoutUrl, userManager } from "../config/cognito";
import { TrustedDevice } from "../types/trustedDevice";
import { UserProfileAuthResponse, UserSignInResponse } from "../types/user";
import { authApiClient } from "./api-client.service";
import { DEVICE_FINGERPRINT_STORAGE_KEY } from "./device-fingerprint.service";
import { ApiService } from "./api.service";
import {
  SessionLogoutReason,
  setSessionLogoutReason,
} from "./session-logout-reason";
import {
  InitiateMfaChallengeResponse,
  MfaMethod,
  ResendMfaChallengeResponse,
  VerifyMfaChallengeResponse,
} from "../types/mfa";
import { refreshUserProfile } from "../context/UserContext";

const COOKIE_CONSENT_STORAGE_KEY = "evoly_cookie_consent_v1";

class AuthService extends ApiService {
  async signIn(role: string, organizationId?: string): Promise<boolean> {
    try {
      const authResponse: UserSignInResponse =
        await this.post<UserSignInResponse>(authApiClient, "/sign-in", {
          role,
          organizationId,
        });

      if (!authResponse?.token || !authResponse?.expiresIn) {
        console.error("Sign-in failed, auth data not provided:", authResponse);
        return false;
      }

      // Store the internal JWT token
      TokenService.setInternalToken(authResponse.token);
      refreshUserProfile();

      return true;
    } catch (error) {
      console.error("Sign-in error:", error);
      return false;
    }
  }

  async logout(reason?: SessionLogoutReason): Promise<void> {
    // Persist the logout reason (if any) into sessionStorage *before* we
    // redirect to Cognito. sessionStorage survives the Cognito round-trip in
    // the same tab and is not cleared by the `localStorage.clear()` below,
    // so the post-logout landing page can read it and display the correct
    // informational toast (inactivity vs. absolute session expiration).
    if (reason) {
      setSessionLogoutReason(reason);
    }

    // Invalidate internal JWT token
    await this.signOut().catch((): void => {});

    // Clear local storage but preserve user-level settings that should survive sign-out.
    const preservedFingerprint: string | null = localStorage.getItem(
      DEVICE_FINGERPRINT_STORAGE_KEY,
    );
    const preservedCookieConsent: string | null = localStorage.getItem(
      COOKIE_CONSENT_STORAGE_KEY,
    );
    await userManager.removeUser().catch((): void => {});
    localStorage.clear();
    if (preservedFingerprint) {
      localStorage.setItem(
        DEVICE_FINGERPRINT_STORAGE_KEY,
        preservedFingerprint,
      );
    }
    if (
      preservedCookieConsent === "accepted" ||
      preservedCookieConsent === "rejected"
    ) {
      localStorage.setItem(
        COOKIE_CONSENT_STORAGE_KEY,
        preservedCookieConsent,
      );
    }

    // Redirect to Cognito logout
    window.location.href = getLogoutUrl();
  }

  async getUserProfileFromAuthApi(): Promise<UserProfileAuthResponse> {
    return this.get<UserProfileAuthResponse>(authApiClient, "/profile");
  }

  private async signOut(): Promise<void> {
    const internalToken: string | null = TokenService.getInternalToken();

    if (!internalToken) {
      return;
    }

    this.post(authApiClient, "/sign-out", { internalToken }).catch(
      (): void => {},
    );
  }

  async initiateMfaChallenge(
    method: MfaMethod,
  ): Promise<InitiateMfaChallengeResponse> {
    return this.post(authApiClient, "/mfa/initiate", { method });
  }

  async verifyMfaChallenge(
    challengeId: string,
    code: string,
  ): Promise<VerifyMfaChallengeResponse> {
    return this.post(authApiClient, "/mfa/verify", { challengeId, code });
  }

  async resendMfaChallenge(
    challengeId: string,
  ): Promise<ResendMfaChallengeResponse> {
    return this.post(authApiClient, "/mfa/resend", { challengeId });
  }

  async getTrustedDevices(): Promise<TrustedDevice[]> {
    return this.get(authApiClient, "/devices", undefined);
  }

  async checkDeviceTrusted(): Promise<{ trusted: boolean }> {
    return this.get(authApiClient, "/devices/check", undefined);
  }

  async trustCurrentDevice(): Promise<TrustedDevice> {
    return this.post<TrustedDevice>(authApiClient, "/devices/trust", {});
  }

  async revokeTrustedDevice(deviceId: string): Promise<{ success: boolean }> {
    return this.delete(authApiClient, `/devices/${deviceId}`);
  }

  async generateTotpSecret(): Promise<{ secret: string; qrCodeUrl: string }> {
    return this.post(authApiClient, "/mfa/totp/generate", {});
  }

  async enableTotp(
    code: string,
  ): Promise<{ success: boolean; backupCodes: string[] }> {
    return this.post(authApiClient, "/mfa/totp/enable", { code });
  }

  async disableTotp(): Promise<{ success: boolean }> {
    return this.post(authApiClient, "/mfa/totp/disable", {});
  }

  async getTotpStatus(): Promise<{ enabled: boolean }> {
    return this.get(authApiClient, "/mfa/totp/status", undefined);
  }

  async initiateEmailMfaSetup(): Promise<{
    challengeId: string;
    expiresAt: string;
  }> {
    return this.post(authApiClient, "/mfa/email/setup/initiate", {});
  }

  async verifyEmailMfaSetup(
    challengeId: string,
    code: string,
  ): Promise<{ success: boolean; backupCodes?: string[] }> {
    return this.post(authApiClient, "/mfa/email/setup/verify", {
      challengeId,
      code,
    });
  }

  async resendEmailMfaSetupCode(
    challengeId: string,
  ): Promise<{ success: boolean; expiresAt: string }> {
    return this.post(authApiClient, "/mfa/email/setup/resend", { challengeId });
  }

  async disableEmailMfa(): Promise<{ success: boolean }> {
    const response = await authApiClient.post<{ success: boolean }>(
      "/mfa/email/disable",
    );
    return response.data;
  }

  async getEmailMfaStatus(): Promise<{ enabled: boolean }> {
    return this.get(authApiClient, "/mfa/email/status", undefined);
  }

  async resetMfa(recoveryCode: string): Promise<{ success: boolean }> {
    return this.post(authApiClient, "/mfa/reset", { recoveryCode });
  }
}

export const authService = new AuthService();
